import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const PERSONAL = [["phone", "Mobile phone", "tel"], ["address", "Home address"], ["date_of_birth", "Date of birth", "date"]];
const EMERGENCY = [["emergency_name", "Emergency contact name"], ["emergency_phone", "Emergency contact phone", "tel"]];
const BANK = [["bank_name", "Bank name"], ["bank_account_holder", "Account holder name"], ["bank_account_number", "Account number"], ["bank_branch", "Branch / transit"]];
const IDFIELDS = [["id_type", "ID type (Passport, National ID…)"], ["id_number", "ID number"]];
const DRIVERFIELDS = [["license_number", "Driver's licence number"], ["license_expiry", "Licence expiry", "date"]];

function Section({ title, icon, children }) {
  const Icon = Icons[icon] || Icons.Circle;
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3">
      <p className="font-display font-bold flex items-center gap-2 text-sm"><Icon className="w-4 h-4 text-turquoise" /> {title}</p>
      <div className="grid sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Field({ k, label, type, form, set }) {
  return (
    <div>
      <Label className="text-xs text-white/60">{label}</Label>
      <Input type={type || "text"} value={form[k] || ""} onChange={set(k)} className="rounded-xl bg-white/10 border-white/20 text-white" data-testid={`profile-${k}`} />
    </div>
  );
}

export default function StaffProfile() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({});
  const [meta, setMeta] = useState({ role: "", email: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [docName, setDocName] = useState("");
  const [docUrl, setDocUrl] = useState("");
  const photoRef = useRef(null);
  const docRef = useRef(null);

  const load = () => api.get("/staff/profile").then((r) => {
    const p = r.data.profile || {};
    setForm({ name: r.data.name, ...p });
    setMeta({ role: r.data.role, email: r.data.email, name: r.data.name });
    setPhotoUrl(p.photo_url || "");
    setDocName(p.id_document_name || "");
    setDocUrl(p.id_document_url || "");
  }).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isDriver = meta.role === "driver";

  const save = async () => {
    setBusy(true);
    try { await api.put("/staff/profile", form); toast.success("Profile saved"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file, file.name);
    try { const { data } = await api.post("/staff/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } }); setPhotoUrl(data.photo_url + "?t=" + Date.now()); toast.success("Photo uploaded"); }
    catch (err) { toast.error(apiErr(err)); } finally { e.target.value = ""; }
  };
  const uploadDoc = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file, file.name);
    try { const { data } = await api.post("/staff/profile/document", fd, { headers: { "Content-Type": "multipart/form-data" } }); setDocUrl(data.id_document_url); setDocName(data.id_document_name); toast.success("Document uploaded"); }
    catch (err) { toast.error(apiErr(err)); } finally { e.target.value = ""; }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <header className="h-16 flex items-center justify-between gap-2 px-4 border-b border-white/10">
        <div className="flex items-center gap-2 shrink-0"><img src={LOGO} className="h-9 w-9 object-contain" alt="logo" /><p className="font-display font-bold text-sm hidden sm:block">MY PROFILE</p></div>
        <div className="flex items-center gap-1 overflow-x-auto [&>button]:shrink-0">
          <Button variant="ghost" size="sm" className="text-white" onClick={() => nav(isDriver ? "/driver" : "/watcher")} data-testid="profile-back-btn">Back</Button>
          <Button variant="ghost" size="icon" onClick={async () => { await logout(); nav("/"); }} data-testid="profile-logout"><Icons.LogOut className="w-5 h-5" /></Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-4 pb-24">
        <div>
          <h1 className="font-display font-extrabold text-xl">{meta.name}</h1>
          <p className="text-white/50 text-sm capitalize">{meta.role} · {meta.email}</p>
          <p className="text-white/40 text-xs mt-1">Keep your details up to date so the team can reach you and pay you correctly. This info is private to you and the owner.</p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-4">
          <div className="w-20 h-20 rounded-2xl bg-white/10 overflow-hidden flex items-center justify-center shrink-0">
            {photoUrl ? <img src={photoUrl} alt="" className="w-full h-full object-cover" data-testid="profile-photo-img" /> : <Icons.User className="w-8 h-8 text-white/40" />}
          </div>
          <div>
            <p className="font-semibold text-sm">Profile photo</p>
            <p className="text-white/40 text-xs mb-2">A clear headshot for your staff ID.</p>
            <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="profile-photo-input" />
            <Button size="sm" onClick={() => photoRef.current?.click()} className="rounded-full bg-turquoise text-navy text-xs font-bold" data-testid="profile-photo-btn"><Icons.Upload className="w-3 h-3 mr-1" /> Upload photo</Button>
          </div>
        </div>

        <Section title="About you" icon="User">
          <Field k="name" label="Full name" form={form} set={set} />
          {PERSONAL.map(([k, l, t]) => <Field key={k} k={k} label={l} type={t} form={form} set={set} />)}
        </Section>

        <Section title="Emergency contact" icon="Phone">
          {EMERGENCY.map(([k, l, t]) => <Field key={k} k={k} label={l} type={t} form={form} set={set} />)}
        </Section>

        <Section title="Bank account (for payroll)" icon="Landmark">
          {BANK.map(([k, l, t]) => <Field key={k} k={k} label={l} type={t} form={form} set={set} />)}
        </Section>

        <Section title={isDriver ? "ID & driver's licence" : "Identification"} icon="IdCard">
          {IDFIELDS.map(([k, l, t]) => <Field key={k} k={k} label={l} type={t} form={form} set={set} />)}
          {isDriver && DRIVERFIELDS.map(([k, l, t]) => <Field key={k} k={k} label={l} type={t} form={form} set={set} />)}
        </Section>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3">
          <p className="font-display font-bold flex items-center gap-2 text-sm"><Icons.FileText className="w-4 h-4 text-turquoise" /> {isDriver ? "Driver's licence / ID document" : "ID document"}</p>
          <p className="text-white/40 text-xs">Upload a photo or scan of your {isDriver ? "driver's licence" : "government ID"}. Accepted: image or PDF.</p>
          {docUrl && <a href={docUrl} target="_blank" rel="noreferrer" className="text-turquoise text-sm underline block" data-testid="profile-doc-link"><Icons.Paperclip className="w-3 h-3 inline mr-1" />{docName || "View uploaded document"}</a>}
          <input ref={docRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={uploadDoc} data-testid="profile-doc-input" />
          <Button size="sm" onClick={() => docRef.current?.click()} className="rounded-full bg-white/15 text-white text-xs" data-testid="profile-doc-btn"><Icons.Upload className="w-3 h-3 mr-1" /> {docUrl ? "Replace document" : "Upload document"}</Button>
        </div>

        <Section title="Anything else we should know" icon="StickyNote">
          <div className="sm:col-span-2">
            <Textarea value={form.notes || ""} onChange={set("notes")} rows={3} className="rounded-xl bg-white/10 border-white/20 text-white" data-testid="profile-notes" placeholder="Availability, T-shirt size, certifications, etc." />
          </div>
        </Section>

        <Button onClick={save} disabled={busy} className="w-full rounded-full bg-gold text-navy font-bold h-12" data-testid="profile-save-btn">{busy ? <Icons.Loader2 className="w-5 h-5 animate-spin" /> : "Save my profile"}</Button>
      </main>
    </div>
  );
}
